import useSWR from 'swr';
import axiosInstance from '@/lib/axios';

// Base interface for common user fields from joins
interface BaseStat {
  id: string;
  createdAt: string;
  fromUserId: string;
  toUserId?: string | null; // some tables might be nullable
  fromUserName?: string;
  fromUserLastName?: string;
  toUserName?: string;
  toUserLastName?: string;
}

export interface LeadStat extends BaseStat {
  title: string;
  description: string; // text usually
  timeline: string;
  type: 'SELF' | 'SOMEONE_ELSE';
  personName?: string | null;
  contactNumber?: string | null;
  emailId?: string | null;
  relation?: string | null;
  imageLink?: string;
  attachments?: string | string[]; // Kysely JSON or parsed
  toBusinessId?: string | null;
}

export interface ThanksStat extends BaseStat {
  amount: number;
  message: string;
  businessType?: string; // from enum
  businessFrequencyType?: string;
  fromBusinessId?: string | null;
  // extended fields potentially present
  personName?: string;
  contactNumber?: string;
  attachments?: string | string[];
}

export interface TestimonialStat extends BaseStat {
  title: string;
  notes: string;
  toBusinessId?: string | null;
  fromBusinessId?: string | null;
  // detailed-view uses rating, but strict type does not show it.
  // keeping it optional if needed or removing if strictly following schema.
  // schema says no rating, but UI expects it. keeping optional for now to avoid break.
  rating?: number;
  attachments?: string | string[];
}

export interface MeetupStat extends BaseStat {
  meetupId: string;
  type: 'ONLINE' | 'OFFLINE';
  location: string;
  otherLocation?: string | null;
  date: string;
  time: string;
  duration?: string | null;
  attachments?: string | string[];
  initiatorId: string;
  participantCount: number;
}

interface BusinessStatsResponse {
  isMeetupEnabled: boolean;
  stats: {
    leads: { given: number; received: number };
    thanks: { given: number; received: number; givenAmount: number; receivedAmount: number };
    testimonials: { given: number; received: number };
    meetups: { initiated: number; participated: number };
  };
  recentActivity: {
    leads: LeadStat[];
    thanks: ThanksStat[];
    testimonials: TestimonialStat[];
    meetups: MeetupStat[];
  };
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

export function useBusinessStats(
  communityId: string,
  timeFilter?: string,
  scope?: string,
  fromDate?: Date | null,
  toDate?: Date | null
) {
  const isCustom = timeFilter === 'custom';
  const dateParams =
    isCustom && fromDate && toDate
      ? `&fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`
      : '';

  const { data, error, isLoading, isValidating } = useSWR<BusinessStatsResponse>(
    communityId
      ? `/api/communities/${communityId}/stats?timeFilter=${timeFilter || 'all'}&scope=${scope || 'my'}${dateParams}`
      : null,
    fetcher,
    { keepPreviousData: true }
  );

  return {
    isMeetupEnabled: data?.isMeetupEnabled ?? false,
    stats: data?.stats,
    recentActivity: data?.recentActivity,
    isLoading,
    isValidating,
    isRefreshing: isValidating && !!data,
    error,
  };
}
